import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { date, dateTime, mobile as fmtMobile, num, rupees } from '../lib/format';
import { DataTable, useListParams } from '../components/DataTable';
import type { Column } from '../components/DataTable';
import {
  Badge,
  Button,
  Card,
  CardHead,
  Chips,
  Field,
  Input,
  Modal,
  Notice,
  SearchInput,
  Select,
  Textarea,
  useToast,
} from '../components/ui';
import type { BadgeTone } from '../components/ui';

type FreeStatus = 'PENDING' | 'ACTIVE' | 'SUSPENDED' | 'REJECTED' | 'EXPIRED' | 'DRAFT' | 'DELETED';
type Filter = 'ALL' | 'PENDING' | 'ACTIVE' | 'SUSPENDED' | 'REJECTED' | 'EXPIRED';

interface FreePostRow {
  id: string;
  title: string;
  description: string;
  address: string;
  priceInr: string | null;
  images: string[];
  status: string;
  freeStatus: FreeStatus;
  createdAt: string;
  liveAt: string | null;
  expiresAt: string | null;
  daysRemaining: number | null;
  rejectReason: string | null;
  viewCount: number;
  category: { id: string; name: string; sector?: string; parent?: { id: string; name: string } | null };
  location: { id: string; name: string } | null;
  seller: {
    id: string;
    displayName: string | null;
    mobile: string;
    email: string | null;
    kycStatus: string;
    createdAt: string;
  };
}

interface Rules {
  enabled: boolean;
  validityDays: number;
  categoryIds: string[];
  requiresApproval: boolean;
  buttonText: string;
  promoLabel: string;
  promoLabelTemplate: string;
  categories: Array<{ id: string; name: string; parentId: string | null; sector: string }>;
}

const STATUS: Record<FreeStatus, { label: string; tone: BadgeTone }> = {
  PENDING: { label: 'Pending approval', tone: 'amber' },
  ACTIVE: { label: 'Active', tone: 'green' },
  SUSPENDED: { label: 'Deactivated', tone: 'orange' },
  REJECTED: { label: 'Rejected', tone: 'red' },
  EXPIRED: { label: 'Expired', tone: 'neutral' },
  DRAFT: { label: 'Draft', tone: 'neutral' },
  DELETED: { label: 'Deleted', tone: 'neutral' },
};

/** The sections a free ad is posted in, in the order the app lists them. */
const SECTOR_NAMES: Array<[string, string]> = [
  ['REAL_ESTATE', 'Real Estate'],
  ['BUY_SELL', 'Buy & Sell'],
  ['LOCAL_SERVICE', 'Local Services'],
  ['HOME_SERVICE', 'Home Services'],
  ['JOBS', 'Jobs'],
  ['RENTALS', 'Rentals'],
  ['EDUCATION', 'Education'],
  ['PACKERS_MOVERS', 'Packers & Movers'],
  ['TRAVELS', 'Travels'],
  ['EVENTS', 'Events'],
  ['CATERING', 'Catering'],
];

/** "Buy & Sell › Cars": where an ad is, with the section it sits in. */
function categoryLabel(c: FreePostRow['category']) {
  return c.parent ? `${c.parent.name} › ${c.name}` : c.name;
}

type Dialog =
  | { kind: 'reject' | 'suspend' | 'validity' | 'edit' | 'delete' | 'view'; row: FreePostRow }
  | null;

/**
 * Free Post Management: every account's free ads - one in each sector - and
 * the rules for them.
 *
 * The rules card writes the settings the app reads, so a change here reaches
 * every phone within thirty seconds with no app update. The list below is the
 * free ads themselves, with what an admin can do to each.
 */
export function FreePostsPage() {
  const { get, set } = useListParams();
  const search = get('search');
  const status = (get('status') || 'ALL') as Filter;
  // One parameter for both, so choosing a section clears the category in one step.
  const [sector, categoryId = ''] = get('in').split(':');
  const [dialog, setDialog] = useState<Dialog>(null);
  const toast = useToast();
  const qc = useQueryClient();

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['free-posts'] });
    qc.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const act = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'approve' | 'reinstate' }) =>
      api.post(`/admin/free-posts/${id}/${action}`),
    onSuccess: (_, v) => {
      toast(v.action === 'approve' ? 'Approved — the ad is live' : 'Activated — the ad is live again', 'success');
      refresh();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const columns: Array<Column<FreePostRow>> = [
    {
      key: 'ad',
      header: 'Advertisement',
      render: (r) => (
        <div className="row" style={{ gap: 10, alignItems: 'center' }}>
          {r.images[0] ? (
            <img
              src={r.images[0]}
              alt=""
              style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 8, flex: 'none' }}
            />
          ) : (
            <div
              style={{ width: 52, height: 52, borderRadius: 8, background: 'var(--surface-2, #eef1f6)', flex: 'none' }}
            />
          )}
          <div>
            <div className="cell-strong">{r.title}</div>
            <div className="cell-muted">
              {categoryLabel(r.category)} · {r.priceInr ? rupees(r.priceInr) : 'No price'} · {num(r.images.length)} photo
              {r.images.length === 1 ? '' : 's'}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'seller',
      header: 'Seller',
      render: (r) => (
        <div>
          <Link to={`/users/${r.seller.id}`} className="cell-strong" onClick={(e) => e.stopPropagation()}>
            {r.seller.displayName ?? 'Unnamed'}
          </Link>
          <div className="cell-muted">{fmtMobile(r.seller.mobile)}</div>
          {r.seller.email && <div className="cell-muted">{r.seller.email}</div>}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => (
        <div>
          <Badge tone={STATUS[r.freeStatus].tone} dot>
            {STATUS[r.freeStatus].label}
          </Badge>
          {r.rejectReason && <div className="cell-muted" style={{ marginTop: 4 }}>{r.rejectReason}</div>}
        </div>
      ),
    },
    {
      key: 'posted',
      header: 'Posted',
      render: (r) => (
        <div>
          <div>{date(r.createdAt)}</div>
          <div className="cell-muted">{r.liveAt ? `live ${date(r.liveAt)}` : 'not live yet'}</div>
        </div>
      ),
    },
    {
      key: 'expiry',
      header: 'Validity',
      render: (r) =>
        r.expiresAt ? (
          <div>
            <div className="cell-strong">{date(r.expiresAt)}</div>
            <div className="cell-muted">
              {r.freeStatus === 'ACTIVE' || r.freeStatus === 'SUSPENDED'
                ? `${r.daysRemaining ?? 0} days left`
                : 'ended'}
            </div>
          </div>
        ) : (
          <span className="cell-muted">Starts on approval</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      render: (r) => (
        <div className="cell-actions" onClick={(e) => e.stopPropagation()}>
          {(r.freeStatus === 'PENDING' || r.freeStatus === 'REJECTED') && (
            <Button
              size="sm"
              variant="success"
              loading={act.isPending && act.variables?.id === r.id}
              onClick={() => act.mutate({ id: r.id, action: 'approve' })}
            >
              Approve
            </Button>
          )}
          {(r.freeStatus === 'PENDING' || r.freeStatus === 'ACTIVE' || r.freeStatus === 'SUSPENDED') && (
            <Button size="sm" onClick={() => setDialog({ kind: 'reject', row: r })}>
              Reject
            </Button>
          )}
          {(r.freeStatus === 'ACTIVE' || r.freeStatus === 'PENDING') && (
            <Button size="sm" onClick={() => setDialog({ kind: 'suspend', row: r })}>
              Deactivate
            </Button>
          )}
          {r.freeStatus === 'SUSPENDED' && (
            <Button
              size="sm"
              variant="primary"
              loading={act.isPending && act.variables?.id === r.id}
              onClick={() => act.mutate({ id: r.id, action: 'reinstate' })}
            >
              Activate
            </Button>
          )}
          {r.expiresAt && (
            <Button size="sm" onClick={() => setDialog({ kind: 'validity', row: r })}>
              Validity
            </Button>
          )}
          <Button size="sm" onClick={() => setDialog({ kind: 'edit', row: r })}>
            Edit
          </Button>
          <Button size="sm" variant="danger" onClick={() => setDialog({ kind: 'delete', row: r })}>
            Remove
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="stack">
      <RulesCard />

      <DataTable<FreePostRow>
        queryKey={['free-posts', search, status, sector, categoryId]}
        path="/admin/free-posts"
        params={{ search, status, sector, categoryId }}
        columns={columns}
        onRowClick={(r) => setDialog({ kind: 'view', row: r })}
        emptyIcon="🎁"
        emptyTitle="No free ads here"
        emptyBody="A free ad appears here as soon as somebody posts one."
        toolbar={
          <FilterBar
            status={status}
            onStatus={(v) => set('status', v === 'ALL' ? '' : v)}
            search={search}
            onSearch={(v) => set('search', v)}
            sector={sector}
            onSector={(v) => set('in', v)}
            categoryId={categoryId}
            onCategory={(v) => set('in', v ? `${sector}:${v}` : sector)}
          />
        }
      />

      {dialog?.kind === 'reject' && (
        <ReasonDialog
          title="Reject free ad"
          row={dialog.row}
          required
          action="reject"
          hint="Sent to the seller. Rejecting gives them their free ad back, so they can fix it and post again."
          onClose={() => setDialog(null)}
          onDone={() => {
            setDialog(null);
            toast('Rejected', 'success');
            refresh();
          }}
        />
      )}
      {dialog?.kind === 'suspend' && (
        <ReasonDialog
          title="Deactivate free ad"
          row={dialog.row}
          action="suspend"
          hint="Optional. The ad is hidden from buyers until you activate it again; its end date does not move."
          onClose={() => setDialog(null)}
          onDone={() => {
            setDialog(null);
            toast('Deactivated — hidden from buyers', 'success');
            refresh();
          }}
        />
      )}
      {dialog?.kind === 'validity' && (
        <ValidityDialog
          row={dialog.row}
          onClose={() => setDialog(null)}
          onDone={() => {
            setDialog(null);
            toast('Validity changed', 'success');
            refresh();
          }}
        />
      )}
      {dialog?.kind === 'edit' && (
        <EditDialog
          row={dialog.row}
          onClose={() => setDialog(null)}
          onDone={() => {
            setDialog(null);
            toast('Saved', 'success');
            refresh();
          }}
        />
      )}
      {dialog?.kind === 'delete' && (
        <DeleteDialog
          row={dialog.row}
          onClose={() => setDialog(null)}
          onDone={() => {
            setDialog(null);
            toast('Removed', 'success');
            refresh();
          }}
        />
      )}
      {dialog?.kind === 'view' && <ViewDialog row={dialog.row} onClose={() => setDialog(null)} />}
    </div>
  );
}

/**
 * The status chips carry the count for each state, from the list's own meta,
 * for the section and category chosen beside them.
 */
function FilterBar({
  status,
  onStatus,
  search,
  onSearch,
  sector,
  onSector,
  categoryId,
  onCategory,
}: {
  status: Filter;
  onStatus: (v: Filter) => void;
  search: string;
  onSearch: (v: string) => void;
  sector: string;
  onSector: (v: string) => void;
  categoryId: string;
  onCategory: (v: string) => void;
}) {
  const { data } = useQuery({
    queryKey: ['free-posts', 'counts', search, sector, categoryId],
    queryFn: () => {
      const q = new URLSearchParams({ limit: '1' });
      if (search) q.set('search', search);
      if (sector) q.set('sector', sector);
      if (categoryId) q.set('categoryId', categoryId);
      return api.get<{ meta: { counts: Record<string, number> } }>(`/admin/free-posts?${q}`);
    },
  });
  const { data: rules } = useQuery({ queryKey: ['free-post-settings'], queryFn: () => api.get<Rules>('/admin/free-posts/settings') });
  const c = data?.meta.counts;
  const inSector = (rules?.categories ?? []).filter((x) => x.sector === sector);
  return (
    <div className="row row--wrap" style={{ gap: 'var(--s4)' }}>
      <Chips<Filter>
        value={status}
        options={[
          { value: 'ALL', label: 'All' },
          { value: 'PENDING', label: 'Pending', count: c?.pending },
          { value: 'ACTIVE', label: 'Active', count: c?.active },
          { value: 'SUSPENDED', label: 'Deactivated', count: c?.suspended },
          { value: 'REJECTED', label: 'Rejected', count: c?.rejected },
          { value: 'EXPIRED', label: 'Expired', count: c?.expired },
        ]}
        onChange={onStatus}
      />
      <Select
        value={sector}
        onChange={(e) => onSector(e.target.value)}
        placeholder="All sections"
        options={SECTOR_NAMES.map(([value, label]) => ({ value, label }))}
      />
      {sector && inSector.length > 0 && (
        <Select
          value={categoryId}
          onChange={(e) => onCategory(e.target.value)}
          placeholder="All categories"
          options={[...inSector.filter((x) => !x.parentId), ...inSector.filter((x) => x.parentId)].map((x) => ({
            value: x.id,
            label: x.parentId ? x.name : `All of ${x.name}`,
          }))}
        />
      )}
      <SearchInput value={search} onChange={onSearch} placeholder="Title, seller name or mobile…" />
    </div>
  );
}

/** The settings, edited together and saved together. */
function RulesCard() {
  const toast = useToast();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['free-post-settings'], queryFn: () => api.get<Rules>('/admin/free-posts/settings') });
  const [draft, setDraft] = useState<Rules | null>(null);

  useEffect(() => {
    if (data) setDraft(data);
  }, [data]);

  const save = useMutation({
    mutationFn: (d: Rules) =>
      api.put<Rules>('/admin/free-posts/settings', {
        enabled: d.enabled,
        validityDays: Number(d.validityDays),
        categoryIds: d.categoryIds,
        requiresApproval: d.requiresApproval,
        buttonText: d.buttonText.trim(),
        promoLabel: d.promoLabelTemplate.trim(),
      }),
    onSuccess: (saved) => {
      qc.setQueryData(['free-post-settings'], saved);
      toast('Saved — the app shows it within 30 seconds', 'success');
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  if (!draft) {
    return (
      <Card>
        <div className="skeleton" style={{ height: 160 }} />
      </Card>
    );
  }

  const toggleCategory = (id: string) =>
    setDraft({
      ...draft,
      categoryIds: draft.categoryIds.includes(id) ? draft.categoryIds.filter((x) => x !== id) : [...draft.categoryIds, id],
    });
  const days = Number(draft.validityDays) || 0;
  const preview = draft.promoLabelTemplate.replace(/\{days\}/g, String(days));
  const dirty = JSON.stringify(draft) !== JSON.stringify(data);
  const valid =
    days >= 1 &&
    days <= 365 &&
    draft.buttonText.trim().length >= 2 &&
    draft.buttonText.trim().length <= 24 &&
    draft.promoLabelTemplate.trim().length >= 2 &&
    draft.promoLabelTemplate.trim().length <= 80;

  return (
    <Card>
      <CardHead
        title="Free Post settings"
        sub="Read by the app every time it opens. No app update needed."
        action={
          <div className="row" style={{ gap: 8 }}>
            {dirty && (
              <Button size="sm" onClick={() => data && setDraft(data)}>
                Undo
              </Button>
            )}
            <Button size="sm" variant="primary" disabled={!dirty || !valid} loading={save.isPending} onClick={() => save.mutate(draft)}>
              Save
            </Button>
          </div>
        }
      />
      <div className="stack">
        <div className="row row--wrap" style={{ gap: 'var(--s5)' }}>
          <label className="row" style={{ gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={draft.enabled} onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} />
            <span className="cell-strong">Free Post is on</span>
          </label>
          <label className="row" style={{ gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={draft.requiresApproval}
              onChange={(e) => setDraft({ ...draft, requiresApproval: e.target.checked })}
            />
            <span className="cell-strong">An admin approves each free ad before it is shown</span>
          </label>
        </div>

        <div className="row row--wrap" style={{ gap: 'var(--s5)', alignItems: 'flex-start' }}>
          <Field label="Validity (days)" hint="From the day the ad goes live. 1–365.">
            <Input
              type="number"
              min={1}
              max={365}
              value={String(draft.validityDays)}
              onChange={(e) => setDraft({ ...draft, validityDays: e.target.value as unknown as number })}
            />
          </Field>
          <Field
            label="Free ads per account"
            hint="One per sector (Real Estate, Buy & Sell, Jobs, ...), shared by every category in it; Rentals counts with Real Estate. Used up when it is published; a rejected one is given back."
          >
            <Input value="1 in each sector" readOnly disabled />
          </Field>
          <Field label="Button text" hint="On the app's home screen. Up to 24 characters.">
            <Input value={draft.buttonText} maxLength={24} onChange={(e) => setDraft({ ...draft, buttonText: e.target.value })} />
          </Field>
          <Field label="Promotional label" hint={`{days} becomes the validity. Shows as: ${preview}`}>
            <Input
              value={draft.promoLabelTemplate}
              maxLength={80}
              onChange={(e) => setDraft({ ...draft, promoLabelTemplate: e.target.value })}
            />
          </Field>
        </div>

        <Field
          label="Eligible categories"
          hint={
            draft.categoryIds.length
              ? `${draft.categoryIds.length} chosen. Anything under a chosen category qualifies too.`
              : 'None chosen: every category that ads are posted in qualifies, Jobs included.'
          }
        >
          <div className="stack" style={{ gap: 10 }}>
            {SECTOR_NAMES.map(([key, title]) => {
              const inSector = draft.categories.filter((c) => c.sector === key);
              if (!inSector.length) return null;
              const sorted = [...inSector.filter((c) => !c.parentId), ...inSector.filter((c) => c.parentId)];
              return (
                <div key={key}>
                  <div className="cell-muted" style={{ fontWeight: 700, marginBottom: 4 }}>{title}</div>
                  <div className="row row--wrap" style={{ gap: '6px 18px' }}>
                    {sorted.map((c) => (
                      <label key={c.id} className="row" style={{ gap: 6, cursor: 'pointer' }}>
                        <input type="checkbox" checked={draft.categoryIds.includes(c.id)} onChange={() => toggleCategory(c.id)} />
                        <span className={c.parentId ? '' : 'cell-strong'}>{c.parentId ? c.name : `All of ${c.name}`}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </Field>
        {!draft.enabled && (
          <Notice tone="warn" title="Free Post is off">
            The button is hidden in the app and nobody can post a free ad. Free ads already live stay live.
          </Notice>
        )}
      </div>
    </Card>
  );
}

function ReasonDialog({
  title,
  row,
  action,
  required,
  hint,
  onClose,
  onDone,
}: {
  title: string;
  row: FreePostRow;
  action: 'reject' | 'suspend';
  required?: boolean;
  hint: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const toast = useToast();
  const send = useMutation({
    mutationFn: () => api.post(`/admin/free-posts/${row.id}/${action}`, reason.trim() ? { reason: reason.trim() } : {}),
    onSuccess: onDone,
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const ready = !required || reason.trim().length >= 3;
  return (
    <Modal
      title={title}
      sub={`${row.title} · ${row.seller.displayName ?? fmtMobile(row.seller.mobile)}`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="danger" disabled={!ready} loading={send.isPending} onClick={() => send.mutate()}>
            {action === 'reject' ? 'Reject' : 'Deactivate'}
          </Button>
        </>
      }
    >
      <Field label={required ? 'Reason' : 'Reason (optional)'} hint={hint}>
        <Textarea rows={3} value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} />
      </Field>
    </Modal>
  );
}

function ValidityDialog({ row, onClose, onDone }: { row: FreePostRow; onClose: () => void; onDone: () => void }) {
  const [days, setDays] = useState('7');
  const [direction, setDirection] = useState<'extend' | 'reduce'>('extend');
  const toast = useToast();
  const n = Math.round(Number(days));
  const valid = Number.isFinite(n) && n >= 1 && n <= 365;
  const send = useMutation({
    mutationFn: () => api.post(`/admin/free-posts/${row.id}/validity`, { days: direction === 'extend' ? n : -n }),
    onSuccess: onDone,
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const ended = row.expiresAt ? new Date(row.expiresAt) < new Date() : false;
  return (
    <Modal
      title="Change validity"
      sub={`${row.title} · ${ended ? 'ended' : 'ends'} ${date(row.expiresAt)}`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!valid} loading={send.isPending} onClick={() => send.mutate()}>
            {direction === 'extend' ? `Extend by ${valid ? n : '…'} days` : `Reduce by ${valid ? n : '…'} days`}
          </Button>
        </>
      }
    >
      <div className="stack">
        <Chips<'extend' | 'reduce'>
          value={direction}
          options={[
            { value: 'extend', label: 'Extend' },
            { value: 'reduce', label: 'Reduce' },
          ]}
          onChange={setDirection}
        />
        <Field label="Days" hint="1–365.">
          <Input type="number" min={1} max={365} value={days} onChange={(e) => setDays(e.target.value)} />
        </Field>
        {direction === 'extend' && ended && (
          <Notice tone="info" title="This ad has already ended">
            The new end date counts from today, and the ad goes back up.
          </Notice>
        )}
        {direction === 'reduce' && (
          <Notice tone="warn">If the new end date is today or earlier, the ad ends now and is hidden from buyers.</Notice>
        )}
      </div>
    </Modal>
  );
}

function EditDialog({ row, onClose, onDone }: { row: FreePostRow; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const { data: rules } = useQuery({
    queryKey: ['free-post-settings'],
    queryFn: () => api.get<Rules>('/admin/free-posts/settings'),
  });
  const [title, setTitle] = useState(row.title);
  const [description, setDescription] = useState(row.description);
  const [price, setPrice] = useState(row.priceInr ? String(Math.round(Number(row.priceInr))) : '');
  const [address, setAddress] = useState(row.address);
  const [categoryId, setCategoryId] = useState(row.category.id);

  const send = useMutation({
    mutationFn: () =>
      api.patch(`/admin/free-posts/${row.id}`, {
        title: title.trim(),
        description: description.trim(),
        address: address.trim(),
        categoryId,
        ...(price.trim() ? { priceInr: Number(price) } : {}),
      }),
    onSuccess: onDone,
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const valid =
    title.trim().length >= 3 &&
    description.trim().length >= 10 &&
    address.trim().length >= 3 &&
    (!price.trim() || Number(price) >= 0);

  return (
    <Modal
      wide
      title="Edit free ad"
      sub={`${row.seller.displayName ?? fmtMobile(row.seller.mobile)} · posted ${date(row.createdAt)}`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!valid} loading={send.isPending} onClick={() => send.mutate()}>
            Save
          </Button>
        </>
      }
    >
      <div className="stack">
        <Field label="Title" hint="3–120 characters.">
          <Input value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <Field label="Description" hint="At least 10 characters.">
          <Textarea rows={5} value={description} maxLength={5000} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <div className="row row--wrap" style={{ gap: 'var(--s5)' }}>
          <Field label="Price (₹)">
            <Input type="number" min={0} value={price} onChange={(e) => setPrice(e.target.value)} />
          </Field>
          <Field label="Category">
            <Select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              options={(rules?.categories ?? [row.category])
                .filter((c) => !row.category.sector || c.sector === row.category.sector)
                .map((c) => ({ value: c.id, label: c.name }))}
            />
          </Field>
        </div>
        <Field label="Address">
          <Input value={address} maxLength={300} onChange={(e) => setAddress(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

function DeleteDialog({ row, onClose, onDone }: { row: FreePostRow; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const send = useMutation({
    mutationFn: () => api.del(`/admin/free-posts/${row.id}`),
    onSuccess: onDone,
    onError: (e: Error) => toast(e.message, 'error'),
  });
  return (
    <Modal
      title="Remove free ad?"
      sub={row.title}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="danger" loading={send.isPending} onClick={() => send.mutate()}>
            Remove
          </Button>
        </>
      }
    >
      <p style={{ margin: 0, lineHeight: 1.6 }}>
        The ad is taken down everywhere and disappears from the seller's list. It is kept in the database and the
        audit trail. {row.liveAt ? 'It has run, so the seller does not get their free ad back.' : ''}
      </p>
    </Modal>
  );
}

function ViewDialog({ row, onClose }: { row: FreePostRow; onClose: () => void }) {
  return (
    <Modal wide title={row.title} sub={`${categoryLabel(row.category)} · ${STATUS[row.freeStatus].label}`} onClose={onClose} footer={<Button onClick={onClose}>Close</Button>}>
      <div className="stack">
        {row.images.length > 0 && (
          <div className="row row--wrap" style={{ gap: 8 }}>
            {row.images.map((src, i) => (
              <a key={src} href={src} target="_blank" rel="noreferrer">
                <img
                  src={src}
                  alt={i === 0 ? 'Cover' : `Photo ${i + 1}`}
                  style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 10 }}
                />
              </a>
            ))}
          </div>
        )}
        <div className="cell-strong">{row.priceInr ? rupees(row.priceInr) : 'No price'}</div>
        <p style={{ margin: 0, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{row.description}</p>
        <div className="cell-muted">
          {row.address}
          {row.location ? ` · ${row.location.name}` : ''}
        </div>
        <table className="data">
          <tbody>
            <tr>
              <td className="cell-muted">Seller</td>
              <td>
                <Link to={`/users/${row.seller.id}`}>{row.seller.displayName ?? 'Unnamed'}</Link> ·{' '}
                {fmtMobile(row.seller.mobile)}
                {row.seller.email ? ` · ${row.seller.email}` : ''}
              </td>
            </tr>
            <tr>
              <td className="cell-muted">KYC</td>
              <td>{row.seller.kycStatus.replace(/_/g, ' ').toLowerCase()}</td>
            </tr>
            <tr>
              <td className="cell-muted">Member since</td>
              <td>{date(row.seller.createdAt)}</td>
            </tr>
            <tr>
              <td className="cell-muted">Posted</td>
              <td>{dateTime(row.createdAt)}</td>
            </tr>
            <tr>
              <td className="cell-muted">Live</td>
              <td>{row.liveAt ? dateTime(row.liveAt) : '—'}</td>
            </tr>
            <tr>
              <td className="cell-muted">Ends</td>
              <td>{row.expiresAt ? `${dateTime(row.expiresAt)} (${row.daysRemaining ?? 0} days left)` : '—'}</td>
            </tr>
            <tr>
              <td className="cell-muted">Views</td>
              <td>{num(row.viewCount)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </Modal>
  );
}
